import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';

import { EntityCreationResult, EntityTab } from './create-entity-element.types';
import { INITIAL_CREATE_ENTITY_WIZARD_STATE } from './create-entity-wizard.types';

/**
 * Coordinates `CreateEntityElementComponent` and its three entity-form siblings
 * (`OrganizationFormComponent`, `FacilityFormComponent`, `StudyFormComponent`) without the parent
 * holding `viewChild` references into their internals.
 *
 * Deliberately NOT `providedIn: 'root'` (unlike the dashboard stores): it is provided on
 * `CreateEntityElementComponent`, like `CreateEntityConfigService`, because several widget
 * instances on one page must never share state.
 *
 * The parent only ever presses "Save" (`requestSubmit()`) or "+ Add Another" (`addAnother()`) —
 * it has no idea which tab is active or what that tab's form looks like. Each `*FormComponent`
 * reacts to `submitRequest`/`resetRequest` only when the command's `tab` matches its own kind, then
 * validates and hands its value to `CreateEntityCommandService`, which reports the outcome back via
 * `reportCreated()`/`reportError()`. The parent only reads `result`/`errorMessage`/`submitting` to
 * render the shell and dispatch host `CustomEvent`s.
 */
export const CreateEntityWizardStore = signalStore(
    withState(INITIAL_CREATE_ENTITY_WIZARD_STATE),

    withMethods(store => {
        let _nextToken = 0;

        return {
            /** Applies the host's tab filter; if the active tab was filtered out, moves to the first remaining one. */
            setVisibleTabs(tabs: readonly EntityTab[]): void {
                patchState(store, { visibleTabs: tabs, activeTab: tabs.includes(store.activeTab()) ? store.activeTab() : tabs[0] });
            },

            /** Switches the visible tab without touching any form's entered values. */
            selectTab(tab: EntityTab): void {
                patchState(store, { activeTab: tab });
            },

            /**
             * "Save" was pressed. Tells whichever `*FormComponent` matches the active tab to validate and
             * submit itself; a no-op for the Investigator tab (no form, no API) and while a submission is already in flight.
             */
            requestSubmit(): void {
                const _tab = store.activeTab();

                if (_tab === 'investigator' || store.submitting()) {
                    return;
                }

                patchState(store, { submitRequest: { tab: _tab, token: ++_nextToken } });
            },

            /** "+ Add Another" was pressed. Resets whichever tab's form produced `result` and returns to the form view on that same tab. */
            addAnother(): void {
                const _kind = store.result()?.kind;

                if (_kind) {
                    patchState(store, { resetRequest: { tab: _kind, token: ++_nextToken }, activeTab: _kind });
                }

                patchState(store, { result: null, errorMessage: null, view: 'form' });
            },

            /** Called right before a create request is issued. */
            startSubmitting(): void {
                patchState(store, { errorMessage: null, submitting: true });
            },

            /** Called once a create request succeeds. */
            reportCreated(result: EntityCreationResult): void {
                patchState(store, { submitting: false, result, view: 'success' });
            },

            /** Called if a create request fails. */
            reportError(message: string): void {
                patchState(store, { submitting: false, errorMessage: message });
            },

            /**
             * Surfaces/clears the "widget not configured" banner — set directly by `CreateEntityElementComponent`,
             * orthogonal to the per-tab submission errors reported via `reportError()`.
             */
            setConfigErrorMessage(message: string | null): void {
                patchState(store, { errorMessage: message });
            }
        };
    })
);
